import { supabase } from '../lib/supabase';

/**
 * Fare Service - handles all database queries for pricing calculation
 * Acts as intermediary between calculateFare utility and Supabase
 */
export const fareService = {
  /**
   * Get base price for a route from zone_prices table
   * @param {string} fromZone - Origin zone code (A, B, C, etc.)
   * @param {string} toZone - Destination zone code
   * @returns {Promise<number|null>} Base price in THB for MOTORBIKE in daytime/normal weather
   */
  async getBasePrice(fromZone, toZone) {
    try {
      const { data, error } = await supabase?.from('zone_prices')?.select('base_price')?.eq('from_zone', fromZone)?.eq('to_zone', toZone)?.single();

      if (error) {
        console.error('Error fetching base price:', error);
        return null;
      }

      return data?.base_price || null;
    } catch (err) {
      console.error('Exception in getBasePrice:', err);
      return null;
    }
  },

  /**
   * Get transport coefficient from database
   * @param {string} transportType - Service type key (motorbike, sedan, premium, suv4x4, boat, van)
   * @returns {Promise<number|null>} Transport coefficient multiplier
   */
  async getTransportCoefficient(transportType) {
    try {
      const { data, error } = await supabase?.from('transport_coefficients')?.select('coefficient')?.eq('transport_type', transportType)?.single();

      if (error) {
        console.error('Error fetching transport coefficient:', error);
        return null;
      }

      return data?.coefficient ? parseFloat(data?.coefficient) : null;
    } catch (err) {
      console.error('Exception in getTransportCoefficient:', err);
      return null;
    }
  },

  /**
   * Get weather coefficient from database
   * @param {string} weatherType - Weather type: 'normal', 'rain', 'storm'
   * @returns {Promise<number|null>} Weather coefficient multiplier
   */
  async getWeatherCoefficient(weatherType) {
    try {
      const { data, error } = await supabase?.from('weather_coefficients')?.select('coefficient')?.eq('weather_type', weatherType)?.single();

      if (error) {
        console.error('Error fetching weather coefficient:', error);
        return 1.0; // Default to normal weather
      }

      return data?.coefficient ? parseFloat(data?.coefficient) : 1.0;
    } catch (err) {
      console.error('Exception in getWeatherCoefficient:', err);
      return 1.0;
    }
  },

  /**
   * Get all zone codes for route validation
   * @returns {Promise<Array>} Array of zone objects with code and name
   */
  async getAllZones() {
    try {
      const { data, error } = await supabase?.from('zones')?.select('code, name')?.order('code');

      if (error) {
        console.error('Error fetching zones:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Exception in getAllZones:', err);
      return [];
    }
  }
};